import React, { useEffect, useRef, useState } from "react";
import { Chip, Stack } from "@mui/material";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import { t } from "i18next";
import { Player } from "../../shared/interfaces/GameLogicTypes";
import "./FtlStatusBar.css";

export interface FtlStatusBarProps {
  alivePlayers: string[];
  deadPlayers: string[];
  players: { [playerId: string]: Player };
  multiplier: number;
  isMobile?: boolean;
  streakIndex: number;
  aliveLabel?: string;
  deadLabel?: string;
}

const FtlStatusBar = React.memo(function FtlStatusBar({
  alivePlayers,
  deadPlayers,
  players,
  multiplier,
  isMobile,
  streakIndex,
  aliveLabel,
  deadLabel,
}: FtlStatusBarProps) {
  const prevAliveRef = useRef<string[]>(alivePlayers);
  const [dyingPlayers, setDyingPlayers] = useState<string[]>([]);
  const [recentlyDied, setRecentlyDied] = useState<Set<string>>(new Set());

  useEffect(() => {
    const prevAlive = prevAliveRef.current;
    const aliveSet = new Set(alivePlayers);
    const newlyDying = prevAlive.filter((id) => !aliveSet.has(id));

    if (newlyDying.length > 0) {
      setDyingPlayers(newlyDying);

      let arrivalTimer: ReturnType<typeof setTimeout>;

      // After 1s death animation, clear dying and mark as recently arrived
      const deathTimer = setTimeout(() => {
        setDyingPlayers([]);
        setRecentlyDied(new Set(newlyDying));

        // After 0.5s arrival animation, clear recently died
        arrivalTimer = setTimeout(() => {
          setRecentlyDied(new Set());
        }, 500);
      }, 1000);

      prevAliveRef.current = alivePlayers;
      return () => {
        clearTimeout(deathTimer);
        clearTimeout(arrivalTimer);
      };
    }

    prevAliveRef.current = alivePlayers;
  }, [alivePlayers, deadPlayers]);

  const containerClass = `ftl-status-container${isMobile ? " mobile" : ""}`;

  return (
    <div className={containerClass}>
      {/* Still Running */}
      <div className="ftl-status-box-wrapper">
        <div className="ftl-status-icon">
          <DirectionsRunIcon sx={{ fontSize: 18, color: "#92dc67" }} />
        </div>
        <div className="ftl-status-box alive">
          <div className="ftl-status-count">{alivePlayers.length} {aliveLabel ?? t("ftl_still_running")}</div>
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
            {alivePlayers.map((id) => (
              <Chip
                key={id}
                label={players[id]?.name ?? id}
                size="small"
                className="ftl-chip-alive"
                sx={{ bgcolor: "rgba(146,220,103,0.3)" }}
              />
            ))}
            {dyingPlayers
              .filter((id) => !alivePlayers.includes(id))
              .map((id) => (
                <Chip
                  key={id}
                  label={players[id]?.name ?? id}
                  size="small"
                  className="ftl-chip-dying"
                  sx={{ bgcolor: "rgba(146,220,103,0.3)" }}
                />
              ))}
          </Stack>
        </div>
      </div>

      {/* Graveyard */}
      <div className="ftl-status-box-wrapper">
        <div className="ftl-status-icon">
          <span role="img" aria-label="skull">&#128128;</span>
        </div>
        <div className="ftl-status-box dead">
          <div className="ftl-status-count">{deadPlayers.length} {deadLabel ?? t("ftl_graveyard")}</div>
          {multiplier > 1 && (
            <div className="ftl-multiplier-badge">{t("ftl_multiplier")} &times;{multiplier}</div>
          )}
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
            {deadPlayers.map((id) => (
              <Chip
                key={id}
                label={players[id]?.name ?? id}
                size="small"
                className={recentlyDied.has(id) ? "ftl-chip-arriving" : ""}
                sx={{ bgcolor: "rgba(231,81,81,0.2)", opacity: 0.6 }}
              />
            ))}
          </Stack>
        </div>
      </div>
    </div>
  );
});

export default FtlStatusBar;
